import React, { useState, useEffect } from 'react';
import PropTypes from 'prop-types';
import { Form, Checkbox } from 'antd';
import { UploadCard, TermCard } from 'components';
import {
  DocumentExampleCNH,
  DocumentExampleRG,
  DocumentExampleIRPF1,
  DocumentExampleIRPF2,
  DocumentExampleIRPF3,
  DocumentExampleIRPF4,
  DocumentExampleIRPF5,
  DocumentExampleIRPF6,
  DocumentExampleIRPF7,
  DocumentExampleIRPF8,
  DocumentExampleIRPF9,
} from 'assets/images';

import './styles.less';

const { Item } = Form;

const cardIdExamples = [DocumentExampleRG, DocumentExampleCNH];
const irpfExamples = [
  DocumentExampleIRPF1,
  DocumentExampleIRPF2,
  DocumentExampleIRPF3,
  DocumentExampleIRPF4,
  DocumentExampleIRPF5,
  DocumentExampleIRPF6,
  DocumentExampleIRPF7,
  DocumentExampleIRPF8,
  DocumentExampleIRPF9,
];

const DocumentsForm = ({
  form,
  civilStatus,
  businessUser: selectedBusinessUser,
  flow,
  business,
  address,
  necessaryDocumentsFlow,
  lemitValidated,
  isCreation,
  loanStatus,
  finalAmount,
}) => {
  const [idCard, setIdCard] = useState([]);
  const [proofOfIncome, setProofOfIncome] = useState([]);
  const [proofOfIncomeReceipt, setProofOfIncomeReceipt] = useState([]);
  const [proofOfAddress, setProofOfAddress] = useState([]);
  const [proofOfRealState, setProofOfRealState] = useState([]);
  const [proofOfMariageOrDivorce, setProofOfMariageOrDivorce] = useState([]);
  const [partnerIdCard, setPartnerIdCard] = useState([]);

  useEffect(() => {
    if (selectedBusinessUser) {
      const {
        user: {
          idCard,
          proofOfIncome,
          proofOfIncomeReceipt,
          proofOfAddress,
          civilPartner = {},
          proofOfMariageOrDivorce,
          additionalFiles = [],
        },
      } = selectedBusinessUser;

      const { proofOfRealState } = additionalFiles.reduce((acc, file) => {
        if (file.parent?.name) {
          if (!acc[file.parent.name]) {
            acc[file.parent.name] = [];
          }

          acc[file.parent.name].push(file);
        }

        return acc;
      }, {});

      form.setFieldsValue({
        idCardFileId: idCard?.id,
        proofOfIncomeFileId: proofOfIncome?.id,
        proofOfIncomeReceiptFileId: proofOfIncomeReceipt?.id,
        proofOfAddressFileId: proofOfAddress?.id,
        partnerIdCardFileId: civilPartner?.idCard?.id,
        proofOfMariageOrDivorceFileId: proofOfMariageOrDivorce?.id,
        proofOfRealStateIds: proofOfRealState?.map(({ id }) => id),
      });
      if (idCard) setIdCard([idCard]);
      if (proofOfIncome) setProofOfIncome([proofOfIncome]);
      if (proofOfIncomeReceipt) setProofOfIncomeReceipt([proofOfIncomeReceipt]);
      if (proofOfAddress) setProofOfAddress([proofOfAddress]);
      if (civilPartner?.idCard) setPartnerIdCard([civilPartner?.idCard]);
      if (proofOfMariageOrDivorce) { setProofOfMariageOrDivorce([proofOfMariageOrDivorce]); }
      if (proofOfRealState) { setProofOfRealState(proofOfRealState); }
    }
  }, [selectedBusinessUser]);

  return (
    <div id="documents-form">
      <Form form={form}>
        {necessaryDocumentsFlow !== 'B'
          && ((isCreation && loanStatus === 'check-documents')
            || loanStatus !== 'check-documents')
          && (
            <Item
              name="idCardFileId"
              rules={[
                {
                  required: true,
                  message: 'Insira a foto do documento solicitado',
                },
              ]}
            >
              <UploadCard
                id="idCardFileId"
                accept="image/png,image/jpeg,application/pdf"
                label="Identidade, CPF ou CNH"
                actualFile={idCard[0]}
                exampleImages={cardIdExamples}
                alert={(
                  <>
                    O documento deve estar na validade e a foto em boa
                    qualidade. Envie apenas <b>UMA FOTO</b> do documento aberto.
                    Visualize o exemplo para instruções mais detalhadas.
                  </>
                )}
                onCompleted={async ({ id, url, name }) => {
                  await form.setFieldsValue({ idCardFileId: id });
                  setIdCard([{ uid: id, url, name }]);
                }}
              />
            </Item>
          )}
        {((!isCreation
          && (selectedBusinessUser?.user?.proofOfIncome?.analysis
            || selectedBusinessUser?.user?.proofOfIncome?.analysis?.approved === false))
          || (!isCreation && (!selectedBusinessUser?.proofOfIncome))
          || isCreation
          || loanStatus !== 'check-documents')
          && (
            <Item
              name="proofOfIncomeFileId"
              rules={[
                {
                  required: true,
                  message: 'Insira a foto do documento solicitado',
                },
              ]}
            >
              <UploadCard
                id="proofOfIncomeFileId"
                accept="image/png,image/jpeg,application/pdf"
                label="Comprovante de Renda"
                actionButtonText={selectedBusinessUser?.user?.proofOfIncome?.analysis?.approved === false && 'Revisar'}
                incompleteAnimation={
                  selectedBusinessUser?.user?.proofOfIncome?.analysis
                    ?.approved === false
                }
                actualFile={proofOfIncome[0]}
                exampleImages={irpfExamples}
                alert={(
                  <>
                    Declaração completa do Imposto de Renda. Caso não tenha
                    declarado, envie comprovante de{' '}
                    <b>NÃO DECLARANTE DE IMPOSTO DE RENDA</b> (
                    <b>
                      <a
                        href="https://servicos.receita.fazenda.gov.br/Servicos/ConsRest/Atual.app/paginas/index.asp"
                        target="_blank"
                        rel="noreferrer"
                      >
                        CLIQUE AQUI
                      </a>
                    </b>{' '}
                    preencha com os seus dados e após visualizar a declaração
                    salve em PDF para que possa ser enviada).
                  </>
                )}
                onCompleted={async ({ id, url, name }) => {
                  await form.setFieldsValue({ proofOfIncomeFileId: id });
                  setProofOfIncome([{ uid: id, url, name }]);
                }}
              />
            </Item>
          )}
        {(isCreation || loanStatus !== 'check-documents') && (
          <Item
            name="proofOfIncomeReceiptFileId"
            rules={[
              {
                required: true,
                message: 'Insira a foto do documento solicitado',
              },
            ]}
          >
            <UploadCard
              id="proofOfIncomeReceiptFileId"
              accept="image/png,image/jpeg,application/pdf"
              label="Recibo do Comprovante de Renda"
              actualFile={proofOfIncomeReceipt[0]}
              alert={(
                <>
                  Recibo da Declaração Imposto de Renda. Caso não tenha
                  declarado, envie recibo de comprovante de{' '}
                  <b>NÃO DECLARANTE DE IMPOSTO DE RENDA</b>.
                </>
              )}
              onCompleted={async ({ id, url, name }) => {
                await form.setFieldsValue({ proofOfIncomeReceiptFileId: id });
                setProofOfIncomeReceipt([{ uid: id, url, name }]);
              }}
            />
          </Item>
        )}

        {(necessaryDocumentsFlow !== 'B' || (!lemitValidated || loanStatus === 'check-documents'))
          && (loanStatus !== 'check-documents'
            || isCreation
            || (!isCreation
              && (selectedBusinessUser?.user?.address?.analysis
                || selectedBusinessUser?.user?.address?.analysis?.approved === false))
            || (!isCreation && (!selectedBusinessUser?.user?.address))
          )
          && (
            <Item
              name="proofOfAddressFileId"
              rules={[
                {
                  required: true,
                  message: 'Insira a foto do documento solicitado',
                },
              ]}
            >
              <UploadCard
                id="proofOfAddressFileId"
                accept="image/png,image/jpeg,application/pdf"
                label="Comprovante de Residência"
                actionButtonText={selectedBusinessUser?.user?.address?.analysis?.approved === false && 'Revisar'}
                incompleteAnimation={
                  selectedBusinessUser?.user?.address?.analysis?.approved === false
                }
                actualFile={proofOfAddress[0]}
                alert={(
                  <>
                    Escolha entre: Conta de luz, água, gás, telefone ou
                    internet, com <b>no máximo 90 dias</b>, ou contrato de
                    locação do imóvel. Envie apenas <b>UMA FOTO</b> da conta
                    aberta. Não aceitamos somente a capa.
                  </>
                )}
                onCompleted={async ({ id, url, name }) => {
                  await form.setFieldsValue({ proofOfAddressFileId: id });
                  setProofOfAddress([{ uid: id, url, name }]);
                }}
              />
            </Item>
          )}

        {flow === 'badesc'
          && finalAmount > 100000
          && (
            <Item
              name="proofOfRealStateIds"
              rules={[
                {
                  required: true,
                  message: 'Insira a foto do documento solicitado',
                },
              ]}
            >
              <UploadCard
                id="proofOfRealStateIds"
                accept="image/png,image/jpeg,application/pdf"
                label="Matrículas dos imóveis do Cartório Registro de Imóveis"
                actualFile={proofOfRealState}
                multiples
                onCompleted={async ({ id }) => {
                  const ids = form.getFieldValue('proofOfRealStateIds') || [];
                  ids.push(id);

                  await form.setFieldsValue({ proofOfRealStateIds: ids });
                }}
                onDelete={async ({ id }) => {
                  const ids = form.getFieldValue('proofOfRealStateIds') || [];
                  const newArrayOfIds = [];
                  ids.map((index) => index !== id && newArrayOfIds.push(index));

                  await form.setFieldsValue({ proofOfRealStateIds: newArrayOfIds });
                }}
                alert={(
                  <>
                    Nas operações acima de 100 mil reais, será necessário
                    a inclusão de 2 matrículas de imóveis distintas para qualificação do avalista
                  </>
                )}
              />
            </Item>
          )}

        {['casado', 'uniao-estavel'].includes(civilStatus)
          && necessaryDocumentsFlow !== 'B'
          && (isCreation || loanStatus !== 'check-documents')
          && (
            <Item
              name="partnerIdCardFileId"
              rules={[
                {
                  required: true,
                  message: 'Insira a foto do documento solicitado',
                },
              ]}
            >
              <UploadCard
                id="partnerIdCardFileId"
                accept="image/png,image/jpeg,application/pdf"
                label="Identidade ou CNH do cônjuge"
                actualFile={partnerIdCard[0]}
                exampleImages={cardIdExamples}
                alert="O documento deve estar na validade e a foto em boa qualidade. Envie apenas UMA FOTO do documento aberto"
                onCompleted={async ({ id, url, name }) => {
                  await form.setFieldsValue({ partnerIdCardFileId: id });
                  setPartnerIdCard([{ uid: id, url, name }]);
                }}
              />
            </Item>
          )}

        {['casado', 'divorciado'].includes(civilStatus)
          && necessaryDocumentsFlow !== 'B'
          && (isCreation || loanStatus !== 'check-documents')
          && (
            <Item
              name="proofOfMariageOrDivorceFileId"
              rules={[
                {
                  required: true,
                  message: 'Insira a foto do documento solicitado',
                },
              ]}
            >
              <UploadCard
                id="proofOfMariageOrDivorceFileId"
                accept="image/png,image/jpeg,application/pdf"
                label={
                  civilStatus === 'casado'
                    ? 'Certidão de casamento'
                    : 'Averbação da separação ou divórcio'
                }
                actualFile={proofOfMariageOrDivorce[0]}
                alert="A foto do documento deve estar em boa qualidade."
                onCompleted={async ({ id, url, name }) => {
                  await form.setFieldsValue({
                    proofOfMariageOrDivorceFileId: id,
                  });
                  setProofOfMariageOrDivorce([{ uid: id, url, name }]);
                }}
              />
            </Item>
          )}

        {['divorciado'].includes(civilStatus)
          && necessaryDocumentsFlow === 'B'
          && (isCreation || loanStatus !== 'check-documents')
          && (
            <Item
              name="proofOfMariageOrDivorceFileId"
              rules={[
                {
                  required: true,
                  message: 'Insira a foto do documento solicitado',
                },
              ]}
            >
              <UploadCard
                id="proofOfMariageOrDivorceFileId"
                accept="image/png,image/jpeg,application/pdf"
                label="Averbação da separação ou divórcio"
                actualFile={proofOfMariageOrDivorce[0]}
                alert="A foto do documento deve estar em boa qualidade."
                onCompleted={async ({ id, url, name }) => {
                  await form.setFieldsValue({
                    proofOfMariageOrDivorceFileId: id,
                  });
                  setProofOfMariageOrDivorce([{ uid: id, url, name }]);
                }}
              />
            </Item>
          )}

        {flow === 'badesc'
          && ['solteiro', 'uniao-estavel'].includes(civilStatus)
          && (
            <>
              <TermCard
                type={civilStatus}
                alert="A seguir, você assinará um termo que comprova a veracidade do estado civil do sócio. Leia atentamente."
                user={selectedBusinessUser?.user}
                business={business}
                address={address}
              />
              <Item
                name="withProof"
                valuePropName="checked"
                rules={[
                  () => ({
                    validator(_, value) {
                      if (value) {
                        return Promise.resolve();
                      }
                      return Promise.reject(
                        new Error('Você precisa concordar com o termo'),
                      );
                    },
                  }),
                ]}
              >
                <Checkbox>
                  Confirmo que li, aceito e concordo com as informações
                  descritas acima. Reconheço a assinatura deste termo como
                  válida e comprovante do estado civil do sócio adicionado.
                </Checkbox>
              </Item>
            </>
          )}
      </Form>
    </div>
  );
};

DocumentsForm.propTypes = {
  form: PropTypes.shape(),
  businessUser: PropTypes.shape(),
  business: PropTypes.shape(),
  address: PropTypes.oneOfType([PropTypes.shape(), PropTypes.bool]),
  civilStatus: PropTypes.string,
  flow: PropTypes.string,
  necessaryDocumentsFlow: PropTypes.string,
  lemitValidated: PropTypes.bool,
  isCreation: PropTypes.bool,
  loanStatus: PropTypes.string,
};
export default DocumentsForm;
